import { Button, Dropdown } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissionChecker } from '../../hooks/usePermissionChecker';
import { useSelectedSchoolYear } from '../../hooks/useSelectedSchoolYear';
import { useEventSyncStatusQuery, VersionInCorrectionKind } from '../../types/planung-graphql-client-defs';
import { TeachingBlockOverviewModal } from '../TeachingBlockVersion/TeachingBlockVersionsOverview/TeachingBlockOverviewModal';
import { useTeachingBlocks } from '../Teachingblock/hooks/useTeachingBlocks';
import { useTimetables } from '../Timetable/hooks/useTimetables';
import { TimetableVersionsOverviewModal } from '../TimetableVersion/TimetableVersionsOverview/TimetableVersionsOverviewModal';
import styles from './CorrectionNotice.module.scss';

/** Eine andere Person kann eine Korrektur beginnen oder abschließen; so oft wird nachgesehen. */
const REFRESH_MS = 5 * 60 * 1000;

// Versionen sperren und entsperren ändert diese Typen; dann wird der Stand neu geladen
const EVENT_SYNC_STATUS_CONTEXT = {
  additionalTypenames: ['TimetableVersion', 'TimetableVersionMeta', 'TeachingBlockVersion', 'TeachingBlockVersionMeta'],
};

/**
 * Hinweis im Header: Versionen in Korrektur halten Tage des Termin-Abgleichs zurück, bis die Korrektur abgeschlossen
 * ist (planung-backend, docs/plans/2026-11-03-correction-visibility-plan.md). Nur mit Modul Stundenplan und Leserecht;
 * ohne Version in Korrektur unsichtbar. Ein Eintrag öffnet die Versionsübersicht des Plans.
 *
 * Das Backend meldet die Versionen der ganzen Schule; gezeigt werden nur die Stundenpläne und Epochenpläne des gewählten
 * Schuljahres. Ohne gewähltes Schuljahr bleibt der Hinweis unsichtbar.
 */
export const CorrectionNotice = observer(() => {
  const { t } = useTranslation();
  const permissions = usePermissionChecker();
  const schoolYear = useSelectedSchoolYear();
  const visible = !!permissions?.isTimetableEnabled() && !!permissions?.canReadTimetable();

  const [{ data }, refetch] = useEventSyncStatusQuery({
    pause: !visible,
    requestPolicy: 'cache-and-network',
    context: EVENT_SYNC_STATUS_CONTEXT,
  });
  useEffect(() => {
    if (!visible) return;
    const id = setInterval(() => refetch({ requestPolicy: 'network-only' }), REFRESH_MS);
    return () => clearInterval(id);
  }, [visible, refetch]);

  // Pläne des gewählten Schuljahres: nur deren Versionen erscheinen. Der Dialog der Epochenpläne öffnet über den
  // Fach-Container.
  const schoolYearUuid = visible ? schoolYear?.uuid : undefined;
  const { timetables } = useTimetables(schoolYearUuid);
  const { teachingBlocks } = useTeachingBlocks(schoolYearUuid);
  const timetableUuids = useMemo(() => new Set((timetables ?? []).map((timetable) => timetable.uuid)), [timetables]);
  const subjectContainerOf = useMemo(
    () => new Map((teachingBlocks ?? []).map((block) => [block.uuid, block.subjectContainerUuid])),
    [teachingBlocks],
  );

  const [openTimetable, setOpenTimetable] = useState<{ uuid: string; name: string } | null>(null);
  const [openSubjectContainer, setOpenSubjectContainer] = useState<string | null>(null);
  const [menuKey, setMenuKey] = useState<number>(0);

  const versions = (data?.eventSyncStatus.versionsInCorrection ?? []).filter((version) =>
    version.kind === VersionInCorrectionKind.Timetable
      ? timetableUuids.has(version.planUuid)
      : subjectContainerOf.has(version.planUuid),
  );
  if (!visible || versions.length === 0) return null;

  // Eigene Einträge statt DropdownMenu: linksbündig, lange Namen umbrechen. Nach einem Klick schließt das Menü (neu
  // aufgebaut über `menuKey`), der Dialog der Versionen öffnet.
  const choose = (action: () => void) => {
    setMenuKey((key) => key + 1);
    action();
  };

  return (
    <>
      <Dropdown
        key={menuKey}
        trigger={
          <Button className={styles.trigger} hierarchy='tertiary'>
            {t('versionsInCorrection.label', { count: versions.length })}
          </Button>
        }
      >
        <div className={styles.menu}>
          {versions.map((version) => (
            <button
              key={version.versionUuid}
              type='button'
              className={styles.entry}
              onClick={() =>
                choose(() =>
                  version.kind === VersionInCorrectionKind.Timetable
                    ? setOpenTimetable({ uuid: version.planUuid, name: version.planName })
                    : setOpenSubjectContainer(subjectContainerOf.get(version.planUuid) ?? null),
                )
              }
            >
              {t('versionsInCorrection.entry', { plan: version.planName, version: version.version })}
            </button>
          ))}
        </div>
      </Dropdown>
      <TimetableVersionsOverviewModal
        timetableUuid={openTimetable?.uuid ?? null}
        draftName={openTimetable?.name ?? ''}
        isOpen={!!openTimetable}
        onClose={() => setOpenTimetable(null)}
      />
      {openSubjectContainer && (
        <TeachingBlockOverviewModal
          currentSubjectContainerUuid={openSubjectContainer}
          isOpen={!!openSubjectContainer}
          onClose={() => setOpenSubjectContainer(null)}
        />
      )}
    </>
  );
});

CorrectionNotice.displayName = 'CorrectionNotice';
